<template>
  <div class="residency">
    <v-container v-if="pageLoading" >
      <v-mockup widget="article-detail" />
    </v-container>
    <div v-else>
      <section class="residency__top">
        <v-container>
          <img src="@/assets/img/residency/top-illustration.svg" alt="Residência">
          <v-heading level="h2">{{page.title}}</v-heading>
          <p v-html="page.subtitle"></p>
        </v-container>
      </section>
      <section class="residency__notices">
        <v-container>
          <v-heading level="h3">Sobre o curso</v-heading>
          <body-text v-html="page.description" />
          <aside>
            <programatic-content v-if="page.e1 || page.e2"
              :contents="{e1: page.e1, e4: page.e4}"/>
            <v-mockup v-if="noticesLoading" tag="aside" :counter="2" widget="article" />
            <div v-else>
              <notice-module v-for="(noticeModule, index) in notices" :key="index"
                v-if="noticeModule.files.length > 0"
                v-bind="noticeModule" />
            </div>
          </aside>
        </v-container>
      </section>
      <residency-institutions :institutions="page.institutions" />
      <residency-events :events="page.events" />
      <residency-preceptors :preceptors="page.preceptors" />
      <residency-students :years="page.years" :exstudents="page.exstudents" />
      <residency-testimonials :testimonials="page.testimonials"/>
      <featured-articles
        class="publications"
        source="publication"
        href="/publicacoes"
        title="Últimas publicações"/>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import Vue from 'vue';
import Container from '@/components/layout/Container.vue';
import Loader from '@/components/layout/Loader.vue';
import Heading from '@/components/uikit/text/Heading.vue';
import Btn from '@/components/uikit/Btn.vue';
import Mockup from '@/components/uikit/mockup/Mockup.vue';
import BodyText from '@/components/uikit/text/BodyText.vue';
import NoticeModule from '@/components/widgets/NoticeModule.vue';
import FeaturedArticles from '@/components/articles/FeaturedArticles.vue';
import ProgramaticContent from '@/components/widgets/ProgramaticContent.vue';
import ResidencyEvents from '@/pages/residency/ResidencyEvents.vue';
import ResidencyInstitutions from '@/pages/residency/ResidencyInstitutions.vue';
import ResidencyPreceptors from '@/pages/residency/ResidencyPreceptors.vue';
import ResidencyStudents from '@/pages/residency/ResidencyStudents.vue';
import ResidencyTestimonials from '@/pages/residency/ResidencyTestimonials.vue';

const meta = [
  {
    vmid: 'og:title',
    name: 'og:title',
    template: 'Residência',
  },
  {
    vmid: 'description',
    name: 'description',
    content: 'O curso de Aperfeiçoamento em Medicina tem o objetivo de capacitar profissionais para atuar com as modalidades diagnósticas em Radiologia Convencional e Digital, Ultrassonografia Geral e com Doppler, Tomografia Computadorizada (Helicoidal ou Multislice), Ressonância Magnética (baixo campo ou alto campo), Radiologia Intervencionista, Mamografia, PET/CT e Densitometria Óssea.',
  },
  {
    vmid: 'og:description',
    name: 'og:description',
    content: 'O curso de Aperfeiçoamento em Medicina tem o objetivo de capacitar profissionais para atuar com as modalidades diagnósticas em Radiologia Convencional e Digital, Ultrassonografia Geral e com Doppler, Tomografia Computadorizada (Helicoidal ou Multislice), Ressonância Magnética (baixo campo ou alto campo), Radiologia Intervencionista, Mamografia, PET/CT e Densitometria Óssea.',
  },
];

export default {
  name: 'Residency',
  components: {
    'v-container': Container,
    'v-heading': Heading,
    'v-mockup': Mockup,
    'body-text': BodyText,
    'notice-module': NoticeModule,
    'programatic-content': ProgramaticContent,
    'residency-institutions': ResidencyInstitutions,
    'residency-events': ResidencyEvents,
    'residency-preceptors': ResidencyPreceptors,
    'residency-students': ResidencyStudents,
    'residency-testimonials': ResidencyTestimonials,
    'featured-articles': FeaturedArticles,
  },
  metaInfo: {
    title: 'Aperfeiçoamento Médico',
    meta,
  },
  computed: {
    ...mapState({
      page: state => state.residency.content,
      notices: state => state.notices.modules,
    }),
    ...mapGetters({
      pageLoading: 'residency/isLoading',
      noticesLoading: 'notices/isLoading',
    }),
  },
  async created() {
    await this.$store.dispatch('residency/getContent');
    await this.$store.dispatch('notices/getModules');
  },
};
</script>

<style lang="scss" scoped>
$component: '.residency';
#{$component} {
  > div > *:nth-child(odd) {
    background-color: $bg-gray;
  }
  &__top {
    padding: $section-gutter 0;
    background-color: $color-d !important;
    .container {
      display: grid;
      grid-template-columns: auto 170px;
      grid-template-rows: auto;
      align-items: center;
      @media screen and (max-width: #{$brakepoint-medium}) {
        display: block;
        text-align: center;
      }
    }
    img {
      grid-column: 2 / 3;
      grid-row: 1 / 3;
      align-self: center;
      @media screen and (max-width: #{$brakepoint-medium}) {
        margin-bottom: $gutter-xxl;
      }
    }
    .heading  {
      grid-column: 1;
      grid-row: 1;
      color: $color-sys-k;
      margin-bottom: $gutter-xxl
    }
    p {
      grid-column: 1;
      grid-row: 2;
      max-width: 550px;
      margin: 0;
      color: $color-sys-k;
      font-size: $font-size-xl;
      @media screen and (max-width: #{$brakepoint-medium}) {
        margin: auto;
      }
    }
  }
  &__notices .container {
    display: grid;
    grid-template-columns: 4fr 2fr;
    grid-template-rows: auto;
    padding-top: $section-gutter;
    padding-bottom: $section-gutter;
    @media screen and (max-width: #{$brakepoint-large}) {
      grid-template-columns: 6fr 4.5fr;
    }
    @media screen and (max-width: #{$brakepoint-medium}) {
      display: block;
    }
    > .heading {
      grid-column: 1 / 3;
      grid-row: 1;
      color: $color-d;
    }
    .body-text {
      grid-column: 1 / 2;
      grid-row: 2;
      max-width: 544px;
      padding-right: $gutter-xxl;
      @media screen and (max-width: #{$brakepoint-medium}) {
        padding-right: 0;
      }
    }
    aside, .loader {
      grid-column: 2 / 3;
      grid-row: 2;
    }
    aside .card {
      margin-bottom: $gutter-xl;
    }
    .loader {
      margin: 0 auto;
    }
  }
}
</style>
